import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewRef } from '@angular/core';
import axios from 'axios';
import { Video } from '../../../../../models/video';
import { Position } from '../../../../../models/position';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AssetService } from '../../../../../services/asset.service';
import { BookVideoService } from '../../../../../services/book-video.service';
import { ApplicationStateService } from '../../../../../services/application-state.service';
import { ModalService } from '../../../../../services/modal.service';

@Component({
    selector: 'app-page-video',
    templateUrl: './page-video.component.html',
    styleUrls: ['./page-video.component.scss'],
    standalone: true
})
export class PageVideoComponent implements OnInit, OnDestroy, AfterViewInit {

    private static pageMap = new Map<number, Array<number>>();

    @Input() video: Video;

    originalPosition: Position;
    originalZIndex: number;
    expandedPosition: Position;

    toggled: boolean;
    clickedOnImage = false;

    iFrameUrl: SafeResourceUrl;
    videoImageUrl: string;
    videoCollapsedIconUrl = '';

    error = false;

    constructor(public sanitizer: DomSanitizer,
                private changeDetectorRef: ChangeDetectorRef,
                private applicationStateService: ApplicationStateService,
                private modalService: ModalService,
                private videoService: BookVideoService,
                public assetService: AssetService) {
    }

    ngOnInit(): void {
        this.originalPosition = this.video.position;

        this.addToMap();

        setTimeout(() => {

            this.originalZIndex = this.video.zIndex;
            this.getIFrameURL();

            if (!this.originalPosition.w || !this.originalPosition.h) {

                const videoIdsOnPage = PageVideoComponent.pageMap.get(this.video.pageId);
                const indexInArray = videoIdsOnPage.indexOf(this.video.id);

                if (indexInArray < 1) {
                    this.expandedPosition = new Position();
                    this.expandedPosition.x = 2;
                    this.expandedPosition.y = 2;
                    this.expandedPosition.w = 96;
                    this.expandedPosition.h = 46;
                } else {
                    this.expandedPosition = new Position();
                    this.expandedPosition.x = 2;
                    this.expandedPosition.y = 50;
                    this.expandedPosition.w = 96;
                    this.expandedPosition.h = 46;
                }

            } else {
                this.expandedPosition = this.originalPosition;
            }

            if (this.video.url &&
                !this.error &&
                this.applicationStateService.userData.bookAccessCode &&
                this.applicationStateService.bookConfig.bookPermissions.bookVideo.view) {
                this.toggleVideo();
            }

        }, 1);

    }

    ngAfterViewInit(): void {
        this.changeDetectorRef.detach();
    }

    ngOnDestroy(): void {
        this.video.position = this.originalPosition;
        this.video.zIndex = this.originalZIndex;
    }

    toggleVideo(): void {

        if ((!this.applicationStateService.userData.bookAccessCode ||
                !this.applicationStateService.bookConfig.bookPermissions.bookVideo.view)
            && !this.video.canBypassCheck()) {
            this.modalService.openModal(this.video);

            return;
        }

        this.toggled = !this.toggled;
        this.clickedOnImage = false;

        if (this.toggled) {
            this.video.zIndex = this.originalZIndex + 20;
            this.video.position = this.expandedPosition;
        } else {
            this.video.zIndex = this.originalZIndex;
            this.video.position = this.originalPosition;
        }
        this.changeDetectorRef.detectChanges();
    }

    getIFrameURL(): SafeResourceUrl {

        try {
            if (!this.iFrameUrl && !this.error) {

                const videoURL = new URL(this.video.url);
                const searchPart = videoURL.search;
                const searchParams = new URLSearchParams(searchPart);
                const urlHost = videoURL.host;
                const vParam = searchParams.get('v');

                if (urlHost.includes('youtu') && vParam) {

                    this.iFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${vParam}?autoplay=1`);
                    this.videoImageUrl = `https://img.youtube.com/vi/${vParam}/sddefault.jpg`;

                } else if (urlHost.includes('vimeo')) {

                    axios.get(`https://vimeo.com/api/oembed.json?url=${this.video.url}`).then(result => {
                        this.videoImageUrl = result.data.thumbnail_url;
                        if (!(this.changeDetectorRef as ViewRef).destroyed) {
                            this.changeDetectorRef.detectChanges();
                        }
                    });

                    const autoplayParam = 'autoplay=1';

                    if (!this.video.url.includes(autoplayParam)) {

                        if (this.video.url.includes('?')) {
                            this.video.url = `${this.video.url}&${autoplayParam}`;
                        } else {
                            this.video.url = `${this.video.url}?${autoplayParam}`;
                        }
                    }

                    this.iFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.video.url);

                } else {
                    this.iFrameUrl = null;
                    this.error = true;
                }
            }

            if (!(this.changeDetectorRef as ViewRef).destroyed) {
                this.changeDetectorRef.detectChanges();
            }

        } catch (e) {
            console.error(`The video url '${this.video.url}' is not a valid URL`);
            this.iFrameUrl = null;
            this.error = true;
            this.toggled = false;
            this.changeDetectorRef.detectChanges();
        }

        return this.iFrameUrl;
    }

    showVideo(): void {
        this.clickedOnImage = true;
        if (!(this.changeDetectorRef as ViewRef).destroyed) {
            this.changeDetectorRef.detectChanges();
            this.videoService.logUsage(this.video);
        }
    }

    private addToMap(): void {

        if (!PageVideoComponent.pageMap.get(this.video.pageId)) {
            PageVideoComponent.pageMap.set(this.video.pageId, [this.video.id]);
        } else {
            const videosInMap = PageVideoComponent.pageMap.get(this.video.pageId);
            if (!videosInMap.includes(this.video.id)) {
                videosInMap.push(this.video.id);
            }
        }
    }

}
